import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import '../../styles/SalesCommissionsPanel.css';

const money = value => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value || 0));
const share = installment => ({ 1: 40, 2: 30, 3: 30 }[installment] || 0);
const percent = value => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(value || 0));
const effectiveRate = item => Number(item.rate || 0) * (share(item.installment) / 100);
const emptyFilters = () => ({ contract_id: '', beneficiary_id: '', start: '', end: '', status: '', progress: '' });
const commissionName = item => {
  if (item.type === 'implementation') {
    return item.installment === 1
      ? 'Comissão de implantação'
      : `Comissão de acompanhamento — ${item.installment}º mês`;
  }
  return item.installment === 1
    ? 'Comissão de venda'
    : `Comissão pós-venda — ${item.installment}º mês`;
};

export default function SalesCommissionsPanel() {
  const [show, setShow] = useState(false);
  const [report, setReport] = useState({ scope: 'personal', viewer: null, items: [], summary: {}, filters: { clients: [], responsibles: [] } });
  const [config, setConfig] = useState({ rates: [], can_configure: false });
  const [filters, setFilters] = useState(emptyFilters());
  const [isFiltering, setIsFiltering] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [rate, setRate] = useState({
    percentage: '', implementation_percentage: '35', effective_from: new Date().toISOString().slice(0, 10),
  });

  const load = async (activeFilters = filters) => {
    const params = Object.fromEntries(Object.entries(activeFilters).filter(([, value]) => value));
    if (params.progress) {
      [params.type, params.installment] = params.progress.split(':');
      delete params.progress;
    }
    setIsFiltering(true);
    try {
      const [reportResponse, configResponse] = await Promise.all([
        axios.get('/api/sales/commissions', { params }), axios.get('/api/sales/commission-settings'),
      ]);
      setReport(reportResponse.data);
      setConfig(configResponse.data);
    } finally {
      setIsFiltering(false);
    }
  };

  useEffect(() => {
    if (show) {
      const initialFilters = emptyFilters();
      setFilters(initialFilters);
      setShowAdvancedFilters(false);
      load(initialFilters);
    }
  }, [show]);

  const applyFilters = event => {
    event.preventDefault();
    load(filters);
  };

  const clearFilters = () => {
    const initialFilters = emptyFilters();
    setFilters(initialFilters);
    load(initialFilters);
  };

  const saveRate = async event => {
    event.preventDefault();
    const { data } = await axios.post('/api/sales/commission-settings', rate);
    setConfig(data);
  };

  const updateStatus = async (id, status) => {
    await axios.patch(`/api/sales/commissions/${id}/status`, { status });
    await load();
  };

  const labels = { sold: 'Vendido', blocked: 'Bloqueado', available: 'Disponível', approved: 'Aprovado', paid: 'Pago' };
  const statusLabels = {
    blocked: 'Aguardando marco', available: 'Disponível para aprovação', approved: 'Aprovada', paid: 'Paga', reversed: 'Estornada',
  };
  const formatDate = value => value ? new Date(value).toLocaleDateString('pt-BR') : '—';
  const advancedFilterCount = [filters.start, filters.end, filters.progress].filter(Boolean).length;

  return <>
    <button className="sales-report-trigger" onClick={() => setShow(true)}>Vendas e comissões</button>
    <Modal show={show} onHide={() => setShow(false)} size="xl" centered scrollable className="sales-report-modal">
      <Modal.Header closeButton>
        <Modal.Title>{report.scope === 'personal' ? 'Minhas vendas e comissões' : 'Vendas e comissões da equipe'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {report.scope === 'personal' && <div className="sales-personal-intro">
          <strong>Olá, {report.viewer?.name}</strong>
          <span>Acompanhe cada venda e a liberação das suas comissões.</span>
        </div>}

        <Form className="sales-report-filters" onSubmit={applyFilters}>
          <Form.Group className="sales-filter-client"><Form.Label>Cliente</Form.Label><Form.Select value={filters.contract_id} onChange={event => setFilters({ ...filters, contract_id: event.target.value })}>
            <option value="">Todos os clientes</option>
            {(report.filters?.clients || []).map(client => <option key={client.id} value={client.id}>{client.customer_name} · {client.plan_name}</option>)}
          </Form.Select></Form.Group>
          {report.scope === 'team' && <Form.Group><Form.Label>Responsável</Form.Label><Form.Select value={filters.beneficiary_id} onChange={event => setFilters({ ...filters, beneficiary_id: event.target.value })}>
            <option value="">Todos os responsáveis</option>
            {(report.filters?.responsibles || []).map(responsible => <option key={responsible.id} value={responsible.id}>{responsible.name}</option>)}
          </Form.Select></Form.Group>}
          <Form.Group><Form.Label>Status</Form.Label><Form.Select value={filters.status} onChange={event => setFilters({ ...filters, status: event.target.value })}>
            <option value="">Todos os status</option><option value="blocked">Aguardando marco</option><option value="available">Disponível para aprovação</option><option value="approved">Aprovada</option><option value="paid">Paga</option><option value="reversed">Estornada</option>
          </Form.Select></Form.Group>
          <div className="sales-filter-actions">
            <Button type="submit" disabled={isFiltering}>{isFiltering ? 'Filtrando...' : 'Aplicar'}</Button>
            <Button type="button" variant="outline-secondary" onClick={clearFilters} disabled={isFiltering}>Limpar</Button>
            <Button type="button" variant={showAdvancedFilters ? 'primary' : 'outline-primary'} className="sales-advanced-toggle" onClick={() => setShowAdvancedFilters(value => !value)} aria-expanded={showAdvancedFilters}>
              <FontAwesomeIcon icon={faGear} /> Avançados {advancedFilterCount > 0 && <>({advancedFilterCount})</>}
            </Button>
          </div>
          {showAdvancedFilters && <div className="sales-advanced-filters">
            <Form.Group><Form.Label>Liberação inicial</Form.Label><Form.Control type="date" value={filters.start} onChange={event => setFilters({ ...filters, start: event.target.value })} /></Form.Group>
            <Form.Group><Form.Label>Liberação final</Form.Label><Form.Control type="date" value={filters.end} onChange={event => setFilters({ ...filters, end: event.target.value })} /></Form.Group>
            <Form.Group><Form.Label>Progressão</Form.Label><Form.Select value={filters.progress} onChange={event => setFilters({ ...filters, progress: event.target.value })}>
              <option value="">Todas as progressões</option><option value="sale:1">Venda — comissão inicial</option><option value="sale:2">Venda — pós-venda 2º mês</option><option value="sale:3">Venda — pós-venda 3º mês</option><option value="implementation:1">Implantação — comissão inicial</option><option value="implementation:2">Implantação — acompanhamento 2º mês</option><option value="implementation:3">Implantação — acompanhamento 3º mês</option>
            </Form.Select></Form.Group>
          </div>}
        </Form>

        <div className="sales-report-summary">
          {Object.keys(labels).map(key => <div key={key}><small>{labels[key]}</small><strong>{money(report.summary[key])}</strong></div>)}
        </div>

        {config.can_configure && <Form onSubmit={saveRate} className="sales-rate-form">
          <div><strong>Percentuais por vigência</strong><small>Aplicados aos pagamentos confirmados a partir da data.</small></div>
          <Form.Group><Form.Label>Venda (%)</Form.Label><Form.Control required type="number" min="0" max="100" step=".01" placeholder="Ex.: 20" value={rate.percentage} onChange={event => setRate({ ...rate, percentage: event.target.value })} /></Form.Group>
          <Form.Group><Form.Label>Implantação (%)</Form.Label><Form.Control required type="number" min="0" max="100" step=".01" placeholder="Ex.: 35" value={rate.implementation_percentage} onChange={event => setRate({ ...rate, implementation_percentage: event.target.value })} /></Form.Group>
          <Form.Group><Form.Label>Vigência</Form.Label><Form.Control required type="date" value={rate.effective_from} onChange={event => setRate({ ...rate, effective_from: event.target.value })} /></Form.Group>
          <Button type="submit">Salvar percentuais</Button>
        </Form>}

        <p className="sales-commission-help"><strong>Como funciona:</strong> cada contratação pode gerar três comissões independentes. O percentual mostrado em cada linha já é o percentual efetivo sobre a base, sem necessidade de calcular a divisão 40/30/30. A liberação depende do checklist obrigatório no card do lead.</p>
        <div className="sales-rate-history">{config.rates.map(item => <span key={item.id}><b>Venda {item.percentage}% · Implantação {item.implementation_percentage ?? 35}%</b> desde {new Date(`${item.effective_from}T12:00`).toLocaleDateString('pt-BR')}</span>)}</div>

        <Table responsive hover className="sales-report-table">
          <thead><tr><th>Cliente</th><th>Responsável</th><th>Tipo de comissão</th><th>Base</th><th>Valor</th><th>Status e progressão</th><th></th></tr></thead>
          <tbody>{report.items.map(item => <tr key={item.id}>
            <td><strong>{item.customer_name}</strong><small>{item.plan_name}</small></td>
            <td>{item.beneficiary_name || 'Não atribuído'}</td>
            <td className="sales-commission-description"><strong>{commissionName(item)}</strong><small>{percent(effectiveRate(item))}% = {money(item.amount)}</small></td>
            <td>{money(item.base_amount)}</td>
            <td><strong>{money(item.amount)}</strong><small>{percent(effectiveRate(item))}% efetivos sobre {money(item.base_amount)}</small></td>
            <td><span className={`sales-status sales-status--${item.status}`}>{statusLabels[item.status] || item.status}</span><small>{item.status === 'blocked' ? `Liberação prevista: ${formatDate(item.eligibility_at)}` : item.paid_at ? `Pago em ${formatDate(item.paid_at)}` : item.approved_at ? `Aprovado em ${formatDate(item.approved_at)}` : item.available_at ? `Disponível desde ${formatDate(item.available_at)}` : ''}</small></td>
            <td>
              {item.status === 'blocked' && <span className="sales-checklist-pending">Concluir checklist no card do lead</span>}
              {config.can_configure && item.status === 'available' && <Button size="sm" onClick={() => updateStatus(item.id, 'approved')}>Aprovar</Button>}
              {config.can_configure && item.status === 'approved' && <Button size="sm" variant="success" onClick={() => updateStatus(item.id, 'paid')}>Pagar</Button>}
            </td>
          </tr>)}</tbody>
        </Table>
        {!report.items.length && <div className="sales-empty-state"><strong>Nenhum resultado encontrado.</strong><span>Ajuste os filtros ou limpe a pesquisa para visualizar outras comissões.</span></div>}
      </Modal.Body>
    </Modal>
  </>;
}